import React, { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import { FixedSizeGrid as ReactWindowGrid } from 'react-window';
import LoadingSpinner from '../LoadingSpinner';
import EmptyState from '../EmptyState';
import useGridDimensions from '../../../hooks/useGridDimensions';
import { COLORS } from '../../../constants/radar';

const CARD_WIDTH = 350;
const CARD_GAP = 24;

const Grid = ({
  items,
  isLoading,
  isLoadingMore,
  pageInfo,
  renderItem,
  handleItemClick,
  handleGridScroll,
  showLoadingSpinner,
  shouldHideContent,
  loadMoreData,
  gridRef,
  emptyStateTitle = "No Items Found",
  emptyStateDescription = "No items found matching the current search criteria. Try adjusting your search or check back later.",
  rowHeight = 230,
  endOfListMessage = "You've reached the end of the list",
  loadMoreThreshold = 50,
  // When true, measures actual container instead of using window-based calculation
  useContainerDimensions = false,
  // Fallback height offset when useContainerDimensions is true but container not yet measured
  containerHeightOffset = 200,
  // Optional explicit height - overrides all internal height calculations when provided
  height,
  // Optional explicit width - overrides all internal width calculations when provided
  width,
  // Selection-related props passed through itemData for react-window cell updates
  extraItemData = {}
}) => {
  // Get dimensions from window-based hook
  const windowDimensions = useGridDimensions();

  // Container measurement for useContainerDimensions mode
  const containerRef = useRef(null);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    if (!useContainerDimensions) {
      return;
    }

    const container = containerRef.current;
    if (!container) {
      return;
    }

    const updateSize = () => {
      const width = container.offsetWidth;
      const height = container.offsetHeight;
      if (width > 0 && height > 0) {
        setContainerSize({ width, height });
      }
    };

    // Use requestAnimationFrame to ensure layout has been calculated
    const rafId = requestAnimationFrame(() => {
      updateSize();
    });

    const observer = new ResizeObserver(updateSize);
    observer.observe(container);

    return () => {
      cancelAnimationFrame(rafId);
      observer.disconnect();
    };
  }, [useContainerDimensions]);

  // Determine effective dimensions
  // If explicit height prop is provided, use it directly
  // For container mode: use measured size if available, otherwise fall back to window-based with extra offset
  const effectiveHeight = height != null
    ? height
    : useContainerDimensions
      ? (containerSize.height > 0 ? containerSize.height : windowDimensions.gridHeight - containerHeightOffset)
      : windowDimensions.gridHeight - 50;
  const effectiveWidth = width != null
    ? width
    : useContainerDimensions
      ? (containerSize.width > 0 ? containerSize.width : windowDimensions.gridWidth)
      : windowDimensions.gridWidth;

  // Calculate column count based on width
  const columnCount = effectiveWidth > 0
    ? Math.max(1, Math.floor(effectiveWidth / (CARD_WIDTH + CARD_GAP)))
    : windowDimensions.columnCount;
  const cellWidth = effectiveWidth > 0
    ? Math.floor(effectiveWidth / columnCount)
    : windowDimensions.cellWidth;

  // Memoize calculations for grid dimensions and row data
  const gridData = useMemo(() => {
    const hasMoreItems = items.length < (pageInfo?.totalCount || 0);
    const showLoadingRow = isLoadingMore && hasMoreItems;
    const showEndMessageRow = !hasMoreItems && items.length > 0;
    const rowCount = Math.ceil(items.length / columnCount) + (showLoadingRow ? 1 : 0) + (showEndMessageRow ? 1 : 0);
    const baseRows = Math.ceil(items.length / columnCount);
    const loadingRowIndex = showLoadingRow ? baseRows : -1;
    const endMessageRowIndex = showEndMessageRow ? baseRows + (showLoadingRow ? 1 : 0) : -1;

    return {
      showLoadingRow,
      showEndMessageRow,
      rowCount,
      baseRows,
      loadingRowIndex,
      endMessageRowIndex,
      hasMoreItems
    };
  }, [items.length, columnCount, isLoadingMore, pageInfo?.totalCount]);

  // Combined grid scroll handler
  const handleCombinedGridScroll = useCallback((scrollInfo) => {
    // First call the parent's scroll handler
    if (handleGridScroll) {
      handleGridScroll(scrollInfo);
    }

    // Then check if we need to load more
    if (!scrollInfo.scrollUpdateWasRequested && loadMoreData) {
      const gridElement = gridRef.current?._outerRef;
      if (!gridElement) return;

      const isNearBottom =
        gridElement.scrollHeight - scrollInfo.scrollTop - gridElement.clientHeight < loadMoreThreshold;

      // Load more if near bottom and not already loading (check both isLoading and isLoadingMore)
      if (isNearBottom && gridData.hasMoreItems && !isLoadingMore && !isLoading) {
        loadMoreData();
      }
    }
  }, [handleGridScroll, gridData.hasMoreItems, isLoadingMore, isLoading, loadMoreData, gridRef, loadMoreThreshold]);

  // Optimized cell renderer with memoization
  const Cell = useMemo(() => {
    return function CellRenderer({ columnIndex, rowIndex, style, data }) {
      const {
        items,
        columnCount,
        renderItem,
        handleItemClick,
        loadingRowIndex,
        endMessageRowIndex,
        shouldHideContent,
        endOfListMessage
      } = data;

      const index = rowIndex * columnCount + columnIndex;

      // If content should be hidden during scroll restoration, return empty cell
      if (shouldHideContent) {
        return <div style={{...style, padding: '8px', boxSizing: 'border-box'}}></div>;
      }

      // Loading row as a virtual row
      if (rowIndex === loadingRowIndex) {
        if (columnIndex === 0) {
          return (
            <div style={{
              ...style,
              padding: '8px',
              boxSizing: 'border-box',
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gridColumn: `span ${columnCount}`
            }}>
              <LoadingSpinner size={30} />
            </div>
          );
        } else {
          return null;
        }
      }

      // End-of-list message as a virtual row
      if (rowIndex === endMessageRowIndex) {
        if (columnIndex === 0) {
          return (
            <div style={{
              ...style,
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              color: '#64748B',
              fontSize: '14px',
              fontWeight: 500,
              padding: '20px 0',
              backgroundColor: 'white',
              gridColumn: `span ${columnCount}`
            }}>
              <span role="img" aria-label="check" style={{
                display: 'inline-flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: '24px',
                height: '24px',
                backgroundColor: COLORS.PRIMARY,
                color: 'white',
                borderRadius: '50%',
                fontSize: '14px',
                marginRight: '8px'
              }}>✓</span>
              {endOfListMessage}
            </div>
          );
        } else {
          return null;
        }
      }

      // Normal grid items
      if (index >= items.length) {
        return null;
      }

      const item = items[index];
      // Pass extra data (like selection state) to renderItem for proper updates
      const { extraItemData = {} } = data;
      return (
        <div style={{
          ...style,
          padding: '8px',
          boxSizing: 'border-box'
        }}>
          {renderItem({ item, onClick: handleItemClick, ...extraItemData })}
        </div>
      );
    };
  }, []);

  if (isLoading && items.length === 0) {
    return <LoadingSpinner />;
  }

  if (!isLoading && items.length === 0) {
    return (
      <EmptyState
        title={emptyStateTitle}
        description={emptyStateDescription}
      />
    );
  }

  const { rowCount, loadingRowIndex, endMessageRowIndex } = gridData;

  // Always render - use fallback dimensions if container not yet measured
  const hasValidDimensions = effectiveWidth > 0 && effectiveHeight > 0;

  // For container mode, use flex to properly fill parent; otherwise use 100% height
  const containerStyle = useContainerDimensions
    ? { position: 'relative', flex: 1, minHeight: 0, width: '100%', display: 'flex', flexDirection: 'column' }
    : { position: 'relative', height: '100%', display: 'flex', flexDirection: 'column' };

  return (
    <div
      ref={useContainerDimensions ? containerRef : undefined}
      style={containerStyle}
    >
      {hasValidDimensions && (
        <ReactWindowGrid
          ref={gridRef}
          columnCount={columnCount}
          columnWidth={cellWidth}
          height={effectiveHeight}
          rowCount={rowCount}
          rowHeight={rowHeight}
          width={effectiveWidth}
          key={`grid-${columnCount}-${useContainerDimensions}`}
          overscanRowCount={10}
          onScroll={handleCombinedGridScroll}
          itemData={{
            items,
            columnCount,
            renderItem,
            handleItemClick,
            loadingRowIndex,
            endMessageRowIndex,
            shouldHideContent,
            endOfListMessage,
            extraItemData
          }}
        >
          {Cell}
        </ReactWindowGrid>
      )}

      {/* Loading spinner overlay */}
      {(showLoadingSpinner || (isLoading && items.length > 0)) && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          zIndex: 1000
        }}>
          <div style={{
            textAlign: 'center'
          }}>
            <LoadingSpinner size={40} />
          </div>
        </div>
      )}
    </div>
  );
};

export default Grid;
